import React, { FC } from 'react';
import { Card, Image } from 'antd';
import {
  translate,
  translateWithParser,
  translationKeys,
} from 'logic/translations/translations.service';
import Paragraph from 'antd/lib/typography/Paragraph';
import {
  SummaryValueSpan,
  TotalRow,
} from 'src/pages-all/user/user-profile/my-pricing/styles/summary-styles';
import styled from 'styled-components';
import { ALENA_ADDON } from 'assets/images/images';
import Title from 'antd/lib/typography/Title';
import {
  MY_PRICING_FORM_NAME,
  MyPricingFormValues,
  parsePriceWithUnit,
} from 'src/pages-all/user/user-profile/my-pricing/my-pricing.logic';
import { useSelector } from 'react-redux';
import { getFormValues } from 'redux-form';
import { useCouponDiscountPrices } from 'src/pages-all/user/user-profile/my-pricing/hooks/use-coupon-discount-prices';
import { CouponDiscount } from 'src/pages-all/user/user-profile/my-pricing/components/CouponDiscount';

const AddonCard = styled(Card)`
  margin-bottom: 20px;
`;

const AddonContent = styled.div`
  display: flex;
  justify-content: space-between;
`;

const AddonTextContainer = styled.div`
  flex: 1 1 62%;
`;

const AddonImageContainer = styled.div`
  flex: 1 1 38%;
  align-self: center;
`;

const AddonTitle = styled(Title)`
  &.ant-typography {
    font-size: 22px;
    font-weight: 700;
    margin-bottom: 10px;
  }
`;

const AddonDescriptionParagraph = styled(Paragraph)`
  font-size: 14px;
  font-weight: 500;
`;

const IntroParagraph = styled(Paragraph)`
  font-size: 16px;
  font-weight: 700;
`;

type AddonTranslation = typeof translationKeys.pricing.selection.addons.learningCenter;

const AddonElement: FC<{ translation: AddonTranslation; image?: string }> = ({
  translation,
  image,
}) => {
  return (
    <AddonCard>
      <AddonContent>
        <AddonTextContainer>
          <AddonTitle>{translate(translation.title)}</AddonTitle>
          {Object.keys(translation.description).map((key) => (
            <AddonDescriptionParagraph key={key}>
              {translate(translation.description[key])}
            </AddonDescriptionParagraph>
          ))}
        </AddonTextContainer>
        {image && (
          <AddonImageContainer>
            <Image src={image} preview={false} />
          </AddonImageContainer>
        )}
      </AddonContent>
    </AddonCard>
  );
};

export const SubscriptionAlenaAddonSummary: FC = () => {
  const formValues: MyPricingFormValues = useSelector(getFormValues(MY_PRICING_FORM_NAME));
  const isPlanWithAlena = !!formValues?.plan?.alenaAddon;

  const yourPrice = formValues?.plan?.alenaAddon?.price;
  const totalPriceWithoutCoupon = yourPrice?.unitAmount ?? null;

  const priceBeforeCouponDiscountWithUnit = parsePriceWithUnit(
    totalPriceWithoutCoupon,
    yourPrice?.currency
  );
  const {
    absoluteNegativeCouponDiscountWithUnit,
    priceAfterCouponDiscountWithUnit,
    couponDiscountText,
  } = useCouponDiscountPrices(totalPriceWithoutCoupon, yourPrice, ['percent']);

  if (!isPlanWithAlena) return <></>;

  return (
    <>
      <IntroParagraph>
        {translateWithParser(translationKeys.pricing.selection.addons.alwaysIncluded)}
      </IntroParagraph>
      <AddonElement
        translation={translationKeys.pricing.selection.addons.alena}
        image={ALENA_ADDON}
      />
      <AddonElement translation={translationKeys.pricing.selection.addons.learningCenter} />
      <CouponDiscount
        couponDiscountText={couponDiscountText}
        absoluteNegativeCouponDiscountWithUnit={absoluteNegativeCouponDiscountWithUnit}
      />
      <TotalRow>
        <span>{translate(translationKeys.pricing.selection.addons.pricePerMonth)}</span>
        <SummaryValueSpan>
          <del>{translate(translationKeys.pricing.selection.addons.regularPrice)}</del>{' '}
          {priceAfterCouponDiscountWithUnit ?? priceBeforeCouponDiscountWithUnit}
        </SummaryValueSpan>
      </TotalRow>
    </>
  );
};
